import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IMedia } from "src/model/unityObject";
import type { IMediaSearch } from "../state/gallerySlice";
import { fetchFilteredMedias, getMediaSearch, setMediaSearch } from "../state/gallerySlice";
import { MEDIA_LIST_COMPONENT_ID } from "../Gallery";
import type { GalleryPickerConfig } from "../Gallery";
import { InsideGalleryContext } from "./insideGalleryContext";
import type { MediaPickerConstraints } from "./mediaPickerConstraints";
import { MediaPickerModal } from "./MediaPickerModal";

export interface MediaPickerOptions extends MediaPickerConstraints {
  initialSearch?: Partial<IMediaSearch>;
  initialMediaId?: number;
  uploadAccept?: string;
  graphNode?: { nodeId: string; contentType?: string };
  title?: string;
}

export type MediaOrFile = { kind: "media"; media: IMedia } | { kind: "file"; file: File };

interface PickerSession {
  id: number;
  options: MediaPickerOptions;
  acceptsRawFile: boolean;
}

export const useMediaPicker = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const insideGallery = useContext(InsideGalleryContext);
  const currentSearch = useAppSelector(getMediaSearch);

  const [session, setSession] = useState<PickerSession | null>(null);
  const resolverRef = useRef<((result: MediaOrFile | null) => void) | null>(null);
  const searchSnapshotRef = useRef<IMediaSearch | null>(null);
  const currentSearchRef = useRef(currentSearch);
  currentSearchRef.current = currentSearch;
  const sessionCounterRef = useRef(0);

  const settle = useCallback(
    (result: MediaOrFile | null) => {
      const resolve = resolverRef.current;
      if (!resolve) return;
      resolverRef.current = null;
      const snapshot = searchSnapshotRef.current;
      searchSnapshotRef.current = null;
      if (snapshot) {
        dispatch(setMediaSearch(snapshot));
        if (insideGallery) {
          const limit = snapshot.limit ?? 20;
          void dispatch(
            fetchFilteredMedias({
              start: (snapshot.page ?? 0) * limit,
              limit,
              componentId: MEDIA_LIST_COMPONENT_ID,
            }),
          );
        }
      }
      setSession(null);
      resolve(result);
    },
    [dispatch, insideGallery],
  );

  const settleRef = useRef(settle);
  settleRef.current = settle;

  const open = useCallback(
    (options: MediaPickerOptions, acceptsRawFile: boolean) => {
      settleRef.current(null);
      searchSnapshotRef.current = { ...currentSearchRef.current };
      dispatch(
        setMediaSearch({
          query: "",
          page: 0,
          mediaType: "all",
          media360tag: "",
          ...options.initialSearch,
        }),
      );
      sessionCounterRef.current += 1;
      const id = sessionCounterRef.current;
      return new Promise<MediaOrFile | null>((resolve) => {
        resolverRef.current = resolve;
        setSession({ id, options, acceptsRawFile });
      });
    },
    [dispatch],
  );

  const pickMediaOrFile = useCallback((options: MediaPickerOptions) => open(options, true), [open]);

  const pickMedia = useCallback(
    async (options: MediaPickerOptions): Promise<IMedia | null> => {
      const result = await open(options, false);
      return result?.kind === "media" ? result.media : null;
    },
    [open],
  );

  useEffect(() => () => settleRef.current(null), []);

  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    if (lastPathnameRef.current !== pathname) {
      lastPathnameRef.current = pathname;
      settleRef.current(null);
    }
  }, [pathname]);

  const isPickerOpen = session !== null;

  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.("#sidebar")) {
        settleRef.current(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isPickerOpen]);

  let pickerElement: JSX.Element | null = null;
  if (session) {
    const { options, acceptsRawFile } = session;
    const config: GalleryPickerConfig = {
      constraints: {
        allowedMediaTypes: options.allowedMediaTypes,
        required360Tag: options.required360Tag,
      },
      uploadAccept: options.uploadAccept,
      initialMediaId: options.initialMediaId ?? 0,
      graphNode: options.graphNode,
      title: options.title,
      acceptsRawFile,
      onConfirm: (media) => settle({ kind: "media", media }),
      onRawFile: (file) => settle({ kind: "file", file }),
      onCancel: () => settle(null),
    };
    pickerElement = <MediaPickerModal key={session.id} config={config} />;
  }

  return { pickMedia, pickMediaOrFile, isPickerOpen, pickerElement };
};
